import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import MissionTaskCards from "./MissionTaskCards";
import SiteLoader from "pages/components/SiteLoader";
import { getMissionWorkFlowStatistics, sendAPIRequest } from "helpers";
import { USER_ROLES } from "constants";
import MissionTasks from "modules/pvs_activities/components/mission/MissionTasks";
import { MISSION_STATUS, MISSION_STATUS_LIST, MissionCorrespondenceUsersList, MOVEMENTS_STATUS } from "modules/pvs_activities/constants";
import MissionStatus from "modules/pvs_activities/components/mission/MissionStatus";
import MissionCorrespondenceUsers from "modules/pvs_activities/components/mission/MissionCorrespondenceUsers";
import collect from "collect.js";
import CustomCard from "components/common/CustomCard";
import CardBody from "components/common/CardBody";
import Row from "components/common/Row";
import MissionTasksContext from "modules/pvs_activities/components/contexts/MissionTasksContext";
import { Skeleton } from "@mui/material";
import { useGetProcessesQuery } from "modules/pvs_activities/redux/service/processes";

const ActiveMissions = () => {
  const authData = useSelector(s => s.auth.data);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMovementData, setIsLoadingMovementData] = useState(false);
  const [missionData, setMissionData] = useState([]);
  const [movementData, setMovementData] = useState([]);
  const [movementDataAgainstPhases, setMovementDataAgainstPhases] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [missionPhases, setMissionPhase] = useState([]);
  const [missionMovements, setMissionMovements] = useState({});
  const processResData = useGetProcessesQuery();

  useEffect(() => {
    getMission();
    getMovementData();
    handleGroupBYPhasing();
    getProcesses();
    getMissionPhases();
  }, []);

  const getMission = async () => {
    try {
      setIsLoading(true);
      const data = await sendAPIRequest(`/api/pvsa/mission-request/?status=${MISSION_STATUS.REQUEST}`);
      const response = await data.json();
      if (response.success) {
        setIsLoading(false);
        setMissionData(response.data);
      } else {
        setIsLoading(false);
      }
    } catch (e) {
      setIsLoading(false);
      console.log(e);
    }
  };

  const handleGroupingByPhase = (data = []) => {
    // 1: Getting counts of processes in mission
    const mission_process_counts = collect(data)
      .groupBy(item => item?.mission_id)
      .map(group => ({ total_process: collect(group).pluck('id').count(), completed_process: collect(group).where('status', 2).count() }))
      .all();
    const groupedData = collect(data)
      .groupBy(task => task?.mission_id)
      .map(x => x.toArray()).all();
    if (Object.keys(groupedData).length > 0) {
      setMissionMovements(groupedData);
    }
    // 2: Getting current active processes
    const current_active_tasks = data.filter(task =>
      [
        MOVEMENTS_STATUS.pending, MOVEMENTS_STATUS.approval, MOVEMENTS_STATUS.valditation, MOVEMENTS_STATUS.decision, MOVEMENTS_STATUS.skip
      ].includes(parseInt(task.status)) && task.is_enabled);
    // 3: Grouping tasks by mission
    const tasksGroupedByMission = collect(current_active_tasks)
      .groupBy(item => item.mission_id).map(x => x.toArray()).all();
    // 4: Finding max task in each mission and adding process counts and due date status to each task
    const filteredTasks = Object.keys(tasksGroupedByMission).map(key => {
      let max_task = tasksGroupedByMission[key][0];
      let isDueDateExceeded = false;
      let remaining_task = 0;
      tasksGroupedByMission[key].forEach(task => {
        if (task.due_date && new Date() > new Date(task.due_date)) {
          isDueDateExceeded = true;
        }
        if (max_task?.relationProcess?.rank < task.relationProcess?.rank) {
          max_task = task;
        } else if (max_task?.relationProcess?.rank === task.relationProcess?.rank) {
          if (max_task?.relationProcess?.sub_rank < task?.relationProcess?.sub_rank) {
            max_task = task;
          }
        }
      });
      return {
        ...max_task,
        mission_process_counts: mission_process_counts[max_task?.mission_id],
        is_due_date_exceeded: isDueDateExceeded,
        remainingTask: remaining_task
      };
    });
    // 5: Grouping tasks by mission phase
    const groupedByPhases = collect(filteredTasks)
      .groupBy(task => task?.relationForm?.mission_phase_id)
      .map(x => x.toArray()).all();
    // 6: Returning tasks
    return groupedByPhases;
  }


  const getMovementData = async () => {
    try {
      setIsLoadingMovementData(true);
      const data = await sendAPIRequest(`/api/pvsa/movement/?user_id=${authData.id}&form_status=1`);
      const response = await data.json();
      if (response.success) {
        if (
          USER_ROLES.business_admin.includes(parseInt(authData.role_id)) ||
          USER_ROLES.cbd_staff.includes(parseInt(authData?.role_id)) ||
          USER_ROLES.it_admin.includes(parseInt(authData?.role_id))
        ) {
          setMovementData(response.data);
        } else {
          setMovementData(response.data);
        }
      }
      setIsLoadingMovementData(false);
    } catch (e) {
      console.log(e);
      setIsLoadingMovementData(false);
    }
  };

  const handleGroupBYPhasing = async () => {
    try {
      const data = await sendAPIRequest(`/api/pvsa/movement/group-by-phase/?user_id=${authData.id}&form_status=1`);
      const response = await data.json();
      if (response.success) {
        setMovementDataAgainstPhases(response.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const getProcesses = async () => {
    try {
      const data = await sendAPIRequest(`/api/pvsa/process`);
      const response = await data.json();
      if (response.success) {
        setProcesses(response.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const getMissionPhases = async () => {
    try {
      const data = await sendAPIRequest(`/api/list/master/72/?order_by=id&show_in_filter=1`);
      const response = await data.json();
      if (response.success) {
        setMissionPhase(response.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const returnCountForRequiredAndAwatingCompletionTask = (task = {}) => {
    try {
      return getMissionWorkFlowStatistics(task.relationProcess, task, task.mission.mission_manager_id)
    } catch (error) {
      console.log({ error });
    }
  }

  return (
    <>
      {isLoading ? (
        <div className="card-body p-4">
          <SiteLoader position="relative" background="transparent" />
        </div>
      ) : (
        <>

          {
            (USER_ROLES.business_admin.includes(parseInt(authData.role_id))
              || USER_ROLES.cbd_staff.includes(parseInt(authData.role_id))
              || USER_ROLES.it_admin.includes(parseInt(authData.role_id))) && (
              <>
                {missionData?.some((data) => data.mission_manager_id === null) && (
                  <>
                    <div className="card-woah border border-secondary d-flex px-4 py-2 shadow-sm w-100 mt-6">
                      <div className="card-body">
                        <div className="card-title1 lh-1">Assign A Mission Manager</div>
                      </div>
                    </div>
                    <div className="card card-woah border border-secondary shadow rounded-0">
                      <div className="card-body p-4">
                        <div className="row">
                          {missionData
                            ?.filter((data) => data.mission_manager_id === null)
                            ?.map((data, index) => (
                              <MissionTaskCards key={index} missionData={data} />
                            ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {missionPhases?.map((phase, index) => (
                  <>
                    <div className="card-woah border border-secondary d-flex px-4 py-2 shadow-sm w-100 mt-6">
                      <div className="card-body">
                        <div className="card-title1 lh-1">{phase.list_element_name}</div>
                      </div>
                    </div>
                    <div className="card card-woah border border-secondary shadow rounded-0">
                      <div className="card-body p-4">
                        <div className="row">
                          {isLoadingMovementData ? (
                            <>
                              <div className="col-md-4">
                                <Skeleton variant="rounded" width={"100%"} height={80} className="corner-rounded" sx={{ my: 1 }} />
                              </div>
                              <div className="col-md-4">
                                <Skeleton variant="rounded" width={"100%"} height={80} className="corner-rounded" sx={{ my: 1 }} />
                              </div>
                              <div className="col-md-4">
                                <Skeleton variant="rounded" width={"100%"} height={80} className="corner-rounded" sx={{ my: 1 }} />
                              </div>
                            </>
                          ) : (
                            movementDataAgainstPhases[phase.id]
                              ?.map((data, index) => (
                                data.is_enabled ? (
                                  <MissionTaskCards
                                    key={index}
                                    missionData={data.mission}
                                    process={data.relationForm}
                                    mission_process_counts={data?.mission_process_counts}
                                    isDueDateExceeded={data?.is_due_date_exceeded}
                                    missionMovement={missionMovements[data.mission_id] ?? []}
                                    processess={processResData.data}
                                  />
                                ) : ("")
                              ))
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ))}
              </>
            )
          }

          {
            USER_ROLES.experts.includes(parseInt(authData?.role_id)) && (
              <React.Fragment>
                <CustomCard>
                  <CardBody>
                    <Row>
                      {
                        missionData.map((mission, index) => (
                          ((mission.mission_experts && mission.mission_experts.length > 0 &&
                            mission.mission_experts.find(expert => expert?.expert?.user_id === authData?.id && [0, 1].includes(expert.response_status)
                            )))
                          &&

                          <MissionTaskCards key={index} missionData={mission} />
                        ))
                      }
                    </Row>
                  </CardBody>
                </CustomCard>
              </React.Fragment>
            )
          }
          {
            USER_ROLES.delegate_roles_group.includes(parseInt(authData?.role_id)) && (
              <React.Fragment>
                <CustomCard>
                  <CardBody>
                    <Row>
                      {
                        missionData.map((mission, index) => (
                          (parseInt(mission.mission_delegate_id) === parseInt(authData?.id))
                          &&
                          <MissionTaskCards key={index} missionData={mission} />
                        ))
                      }
                    </Row>
                  </CardBody>
                </CustomCard>
              </React.Fragment>
            )
          }
          {
            (USER_ROLES.business_admin.includes(parseInt(authData.role_id))
              || USER_ROLES.cbd_staff.includes(parseInt(authData.role_id))
              || USER_ROLES.it_admin.includes(parseInt(authData.role_id))) && (
              <MissionStatus />
            )
          }
          <>
            <div className="col-12 bg-beige mt-6">
              <div className="card-title1 py-2 text-center text-dark">{t("-1", `My Tasks`)}</div>
            </div>
            <MissionTasksContext
              data={{
                movements: movementData,
                taskTrigger: getMovementData,
                isLoadingMovements: isLoadingMovementData
              }}
            >
              <MissionTasks
                showCountry={true}
                taskTableFilterConfigurations={{
                  showCountry: true,
                  showMissionType: true,
                  showMissionPhase: false,
                  showDueDate: true,
                  showStatus: true,
                  showPersonAssigned: true,
                }}
              />
            </MissionTasksContext>
          </>
          {
            !MissionCorrespondenceUsersList.includes(parseInt(authData.role_id)) && (
              <MissionCorrespondenceUsers />
            )
          }
        </>
      )}
    </>
  );
};

export default ActiveMissions;
